import { HomeWrapperContainer } from "@obg/common.home";
import { RegistrationConfigService } from "@obg/common.registration/sdk";
import { OnlyForBotsGuard, Sitemap } from "@obg/common.sitemap";
import { ExperimentConfigService } from "@obg/core.experiment";
import {
	AlternativeMainPageGuard,
	AwsGeofencingGuard,
	CasinoBlockGuard,
	COMMON_ROUTE,
	HampiGuard,
	SportBlogGuard,
} from "@obg/ngx.common";
import { CORE_ROUTE, LastVisitedProductGuard, ProductType } from "@obg/ngx.core";
import { RouteConfig } from "@obg/ngx.router";
import { ErrorLayoutComponent, InternalErrorComponent, NotFoundContainer } from "@obg/ngx.ui.core/errors";

import { CORE_ROUTES_CONFIG } from "./core/core.routes";
import { CoreLayoutContainer } from "./core/layouts/core-layout/core-layout.container";
import { ChannelNotSupportedComponent } from "./errors/channel-not-supported/channel-not-supported.component";
import { PlatformNotSupportedComponent } from "./errors/platform-not-supported/platform-not-supported.component";
import { RouteQueryGuard } from "./guards/route-query-guard/route-query.guard";

export function createRoutes(
	experimentConfig: ExperimentConfigService,
	registrationConfigService: RegistrationConfigService
): RouteConfig[] {
	return [
		{
			name: CORE_ROUTE.home,
			path: "",
			component: HomeWrapperContainer,
			layout: CoreLayoutContainer,
			data: {
				product: ProductType.Common,
			},
			canActivate: [RouteQueryGuard, LastVisitedProductGuard, AlternativeMainPageGuard],
		},
		{
			name: CORE_ROUTE.notFound,
			path: "not-found",
			component: NotFoundContainer,
			layout: CoreLayoutContainer,
			data: {
				product: ProductType.Common,
			},
		},
		{
			name: CORE_ROUTE.internalError,
			path: "internal-error",
			component: InternalErrorComponent,
			layout: ErrorLayoutComponent,
			data: {
				product: ProductType.Common,
			},
		},
		...CORE_ROUTES_CONFIG,
		{
			name: "sitemap-sportsbook",
			path: "sitemap-sportsbook",
			component: Sitemap,
			layout: CoreLayoutContainer,
			canActivate: [OnlyForBotsGuard],
			data: {
				product: ProductType.Common,
			},
		},
		{
			name: CORE_ROUTE.responsibleGaming.root,
			path: "responsible-gaming",
			loadChildren: () => import("./common/responsible-gaming.module").then((m) => m.AppResponsibleGamingModule),
		},
		{
			name: COMMON_ROUTE.gameRules.root,
			path: "game-rules",
			loadChildren: () => import("./common/game-rules.module").then((m) => m.AppGameRulesModule),
		},
		{
			name: COMMON_ROUTE.complaints,
			path: "complaints",
			loadChildren: () => import("./common/complaints.module").then((m) => m.AppComplaintsModule),
		},
		{
			name: COMMON_ROUTE.legalDisclosure,
			path: "legal-disclosure",
			loadChildren: () => import("./common/legal-disclosure.module").then((m) => m.AppLegalDisclosureModule),
		},
		{
			name: COMMON_ROUTE.aboutUs,
			path: "about-us",
			loadChildren: () => import("./common/about-us.module").then((m) => m.AppAboutUsModule),
		},
		{
			name: COMMON_ROUTE.licenseOverview,
			path: "license-overview",
			loadChildren: () => import("./common/license-overview.module").then((m) => m.AppLicenseOverviewModule),
		},
		{
			name: CORE_ROUTE.customerManagement.root,
			path: "account-settings",
			loadChildren: () => import("./common/account-settings.module").then((m) => m.AppAccountSettingsModule),
		},
		{
			name: CORE_ROUTE.marketingSettings.root,
			path: "marketing-settings",
			loadChildren: () => import("./common/marketing-settings.module").then((m) => m.AppMarketingSettingsModule),
		},
		{
			name: CORE_ROUTE.preferencesSettings.root,
			path: "preferences-settings",
			loadChildren: () =>
				import("./common/preferences-settings.module").then((m) => m.AppPreferencesSettingsModule),
		},
		{
			name: CORE_ROUTE.verification.root,
			path: "verification",
			loadChildren: () => import("./common/verification.module").then((m) => m.AppVerificationModule),
		},
		{
			name: "kyc-method-selection",
			path: "kyc-method-selection",
			loadChildren: () =>
				import("./common/kyc-method-selection.module").then((m) => m.AppKycMethodSelectionModule),
		},
		{
			name: CORE_ROUTE.registration.register,
			path: "register",
			loadChildren: () =>
				registrationConfigService.getSPIDEnabled()
					? import("./common/spid-registration.module").then((m) => m.AppRegistrationModule)
					: import("./common/registration.module").then((m) => m.AppRegistrationModule),
		},
		{
			name: CORE_ROUTE.registration.registerSuccess,
			path: "registration-success",
			loadChildren: () =>
				import("./common/customer-verification.module").then((m) => m.AppCustomerVerificationModule),
		},
		{
			name: CORE_ROUTE.registration.activateAccountWithEmail,
			path: "verify-email/:email/:token",
			loadChildren: () => import("./common/email-verification.module").then((m) => m.AppEmailVerificationModule),
		},
		{
			name: CORE_ROUTE.auth.forgotPassword,
			path: "forgotten-password",
			loadChildren: () => import("./common/forgot-password.module").then((m) => m.AppForgotPasswordModule),
		},
		{
			name: CORE_ROUTE.auth.resetPassword,
			path: "reset-password",
			loadChildren: () => import("./common/reset-password.module").then((m) => m.AppResetPasswordModule),
		},
		{
			name: COMMON_ROUTE.termsAndConditions.root,
			path: "terms-and-conditions",
			loadChildren: () =>
				import("./common/terms-and-conditions.module").then((m) => m.AppTermsAndConditionsModule),
		},
		{
			name: COMMON_ROUTE.personalData,
			path: "personal-data",
			loadChildren: () => import("./common/personal-data.module").then((m) => m.AppPersonalDataModule),
		},
		{
			name: COMMON_ROUTE.privacyPolicy,
			path: "privacy-policy",
			loadChildren: () => import("./common/privacy-policy.module").then((m) => m.AppPrivacyPolicyModule),
		},
		{
			name: COMMON_ROUTE.cookiePolicy,
			path: "cookie-policy",
			loadChildren: () => import("./common/cookie-policy.module").then((m) => m.AppCookiePolicyModule),
		},
		{
			name: COMMON_ROUTE.antiMoneyLaundering,
			path: "anti-money-laundering",
			loadChildren: () =>
				import("./common/anti-money-laundering.module").then((m) => m.AppAntiMoneyLaunderingModule),
		},
		{
			name: CORE_ROUTE.cookieSettings,
			path: "cookie-settings",
			loadChildren: () => import("./common/cookie-settings.module").then((m) => m.AppCookieSettingsModule),
		},
		{
			name: CORE_ROUTE.reward.rewards,
			path: "bonuses",
			loadChildren: () => import("./common/reward.module").then((m) => m.AppRewardModule),
		},
		{
			name: CORE_ROUTE.promotions.root,
			path: "promotions",
			loadChildren: () => import("./common/promotion-lobby.module").then((m) => m.AppPromotionLobbyModule),
		},
		{
			name: COMMON_ROUTE.groupCompanies,
			path: "group-companies",
			loadChildren: () => import("./common/group-companies.module").then((m) => m.AppGroupCompaniesModule),
		},
		{
			name: CORE_ROUTE.loyalty.root,
			path: "loyalty",
			loadChildren: () => import("./common/loyalty.module").then((m) => m.AppLoyaltyModule),
		},
		{
			name: CORE_ROUTE.transactionHistory.all,
			path: "transaction-history",
			loadChildren: () =>
				import("./common/transaction-history.module").then((m) => m.AppTransactionHistoryModule),
		},
		{
			name: CORE_ROUTE.gameRoundHistory,
			path: "game-round-history",
			loadChildren: () => import("./common/game-round-history.module").then((m) => m.AppGameRoundHistoryModule),
		},
		{
			name: CORE_ROUTE.payment.deposit,
			path: "deposit",
			loadChildren: () => import("./common/deposit.module").then((m) => m.AppDepositModule),
		},
		{
			name: CORE_ROUTE.payment.withdraw,
			path: "withdraw",
			loadChildren: () => import("./common/withdraw.module").then((m) => m.AppWithdrawModule),
		},
		{
			name: CORE_ROUTE.oms.messages,
			path: "messages",
			loadChildren: () => import("./common/messages.module").then((m) => m.AppMessagesModule),
		},
		{
			name: CORE_ROUTE.balance,
			path: "balance",
			loadChildren: () => import("./common/balance.module").then((m) => m.AppBalanceModule),
		},
		{
			name: ProductType.Sportsbook,
			path: "sportsbook",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, AwsGeofencingGuard],
			loadChildren: () => {
				if (
					!experimentConfig.isSBB2CEnabled() &&
					(experimentConfig.isSBB2BMfeEnabled() ||
						experimentConfig.isSBB2BExperimentEnabled() ||
						experimentConfig.isSBB2BEnabled())
				) {
					return import("./betting/betting-b2b.module").then((m) => m.BettingB2BModule);
				}

				return import("./betting/betting.module").then((m) => m.BettingModule);
			},
		},
		{
			name: "sport-blog",
			path: "sport-blog",
			canActivate: [SportBlogGuard],
			loadChildren: () => import("./common/sport-blog.module").then((m) => m.SportBlogModule),
		},
		{
			name: "product-block",
			path: "product-block",
			loadChildren: () => import("./gaming/product-block.module").then((m) => m.AppProductBlockModule),
		},
		{
			name: "launch-game",
			path: "launch-game",
			loadChildren: () => import("./gaming/launch-game.module").then((m) => m.AppLaunchGameModule),
		},
		{
			name: ProductType.Casino,
			path: "casino",
			canActivate: [HampiGuard],
			canActivateChild: [HampiGuard, CasinoBlockGuard],
			loadChildren: () => import("./gaming/casino.module").then((m) => m.AppCasinoModule),
		},
		{
			name: ProductType.LiveCasino,
			path: "live-casino",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, AwsGeofencingGuard],
			loadChildren: () => import("./gaming/live-casino.module").then((m) => m.AppLiveCasinoModule),
		},
		{
			name: ProductType.Jackpots,
			path: "jackpots",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, AwsGeofencingGuard],
			loadChildren: () => import("./gaming/jackpots.module").then((m) => m.AppJackpotsModule),
		},
		{
			name: ProductType.Slots,
			path: "slots",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, CasinoBlockGuard, AwsGeofencingGuard],
			loadChildren: () => import("./gaming/slots.module").then((m) => m.AppSlotsModule),
		},
		{
			name: ProductType.SkillGames,
			path: "skill-games",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, CasinoBlockGuard, AwsGeofencingGuard],
			loadChildren: () => import("./gaming/skill-games.module").then((m) => m.AppSkillGamesModule),
		},
		{
			name: ProductType.Poker,
			path: "poker",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, AwsGeofencingGuard],
			loadChildren: () => import("./gaming/poker.module").then((m) => m.AppPokerModule),
		},
		{
			name: ProductType.TurkishPoker,
			path: "turkish-poker",
			loadChildren: () => import("./gaming/turkish-poker.module").then((m) => m.AppTurkishPokerModule),
		},
		{
			name: "poker-inclient",
			path: "poker-inclient",
			loadChildren: () => import("./gaming/poker-inclient.module").then((m) => m.AppPokerInclientModule),
		},
		{
			name: "common-poker-inclient",
			path: "common-poker-inclient",
			loadChildren: () => import("./common/poker-inclient.module").then((m) => m.AppPokerInclientModule),
		},
		{
			name: "casino-inclient",
			path: "casino-inclient",
			loadChildren: () => import("./gaming/casino-inclient.module").then((m) => m.AppCasinoInclientModule),
		},
		{
			name: ProductType.VirtualSports,
			path: "virtual-sports",
			canActivate: [HampiGuard, AwsGeofencingGuard],
			canActivateChild: [HampiGuard, AwsGeofencingGuard],
			loadChildren: () => import("./gaming/virtual-sports.module").then((m) => m.AppVirtualSportsModule),
		},
		{
			name: "kickertechLobby",
			path: "betting",
			loadChildren: () => import("./gaming/kickertech-sb.module").then((m) => m.KickertechSbModule),
		},
		{
			name: "poker-migration",
			path: "poker-migration",
			canActivate: [HampiGuard],
			canActivateChild: [HampiGuard],
			loadChildren: () => import("./gaming/poker-migration.module").then((m) => m.AppPokerMigrationModule),
		},
		{
			name: "platform-not-supported",
			path: "platform-not-supported",
			component: PlatformNotSupportedComponent,
			layout: CoreLayoutContainer,
			data: {
				product: ProductType.Common,
			},
		},
		{
			name: "browser-not-supported",
			path: "browser-not-supported",
			loadChildren: () =>
				import("./errors/browser-not-supported/browser-not-supported.module").then(
					(m) => m.AppBrowserNotSupportedModule
				),
		},
		{
			name: "channel-not-supported",
			path: "channel-not-supported",
			component: ChannelNotSupportedComponent,
			layout: CoreLayoutContainer,
			data: {
				product: ProductType.Common,
			},
		},
		{
			name: CORE_ROUTE.auth.openIdConnect,
			path: "oidc-verify",
			loadChildren: () => import("./common/open-id-connect.module").then((m) => m.AppOpenIdConnectModule),
		},
		{
			name: CORE_ROUTE.auth.redirectionVerify,
			path: "redirection-verify",
			loadChildren: () => import("./common/open-id-connect.module").then((m) => m.AppOpenIdConnectModule),
		},
		{
			name: "horse-racing",
			path: "horse-racing",
			pathMatch: "prefix",
			data: {
				customMetaData: true,
				showSeoFooter: true,
			},
			canActivate: [AwsGeofencingGuard],
			canActivateChild: [AwsGeofencingGuard],
			loadChildren: () => import("./gaming/horse-racing.module").then((m) => m.AppHorseRacingModule),
		},
		{
			name: "gaming-account",
			path: "gaming-account",
			loadChildren: () => import("./common/gaming-account.module").then((m) => m.AppGamingAccountModule),
		},
		{
			name: "play-safe",
			path: "play-safe",
			loadChildren: () => import("./common/play-safe.module").then((m) => m.AppPlaySafeModule),
		},
		{
			name: "gaming-contract",
			path: "gaming-contract",
			loadChildren: () => import("./common/gaming-contract.module").then((m) => m.AppGamingContractModule),
		},
		{
			name: "rules-and-regulations",
			path: "rules-and-regulations",
			loadChildren: () =>
				import("./common/rules-and-regulations.module").then((m) => m.AppRulesAndRegulationsModule),
		},
		{
			name: "account-management",
			path: "account-management",
			loadChildren: () => import("./common/account-management.module").then((m) => m.AppAccountManagementModule),
		},
		{
			name: CORE_ROUTE.responsibleGaming.documentVerification,
			path: "document-verification",
			loadChildren: () =>
				import("./common/caf-doc-authenticator.module").then((m) => m.AppCafDocAuthenticatorModule),
		},
		{
			name: "system-requirements",
			path: "system-requirements",
			loadChildren: () =>
				import("./common/system-requirements.module").then((m) => m.AppSystemRequirementsModule),
		},
		{
			name: "deposits-and-withdrawals",
			path: "deposits-and-withdrawals",
			loadChildren: () =>
				import("./common/deposits-and-withdrawals.module").then((m) => m.AppDepositsAndWithdrawalsModule),
		},
		{
			name: "sportsbook-thirdparty",
			path: "sportsbook-thirdparty",
			loadChildren: () =>
				import("./betting/sportsbook-thirdparty.module").then((m) => m.AppSportsbookThirdpartyModule),
		},
		{
			name: "sportsbook-thirdparty-live",
			path: "sportsbook-thirdparty-live",
			loadChildren: () =>
				import("./betting/sportsbook-thirdparty-live.module").then((m) => m.AppSportsbookThirdpartyLiveModule),
		},
		{
			name: COMMON_ROUTE.press,
			path: "press",
			loadChildren: () => import("./common/press.module").then((m) => m.AppPressModule),
		},
		{
			name: "capture",
			path: "capture",
			loadChildren: () => import("./common/capture.module").then((m) => m.AppCaptureModule),
		},
		{
			name: "financial-statement",
			path: "financial-statement",
			loadChildren: () =>
				import("./common/yearly-financial-report/yearly-financial-report.module").then(
					(m) => m.AppYearlyFinancialReportModule
				),
		},
		{
			name: "**",
			path: "**",
			loadChildren: () => import("./errors/not-found/not-found.module").then((m) => m.AppNotFoundModule),
		},
	];
}
